import { useMemo, useCallback } from 'react';
import { Authorize, NavigateLinks, GlobalButton } from '@/components';
import { ROLES } from '@/components/security/AppRoles';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { commercePageBreadcrumbs } from './config/breadcrumbs';
import { CommerceSettingsSearchBar, CommerceSettingsTable } from './components';
import { useCommerceSettingsFilter } from './hooks';
import { useGetCommerceByIdQuery } from './services';
import { CommerceSettingsFilters } from './models';

const CommerceSettings = () => {
  const navigate = useNavigate();
  const { commerceId } = useParams<{ commerceId: string }>();

  const { data: commerceData } = useGetCommerceByIdQuery(
    { commerceId: commerceId || '' },
    { skip: !commerceId }
  );

  const breadcrumbs = useMemo(
    () => [
      ...commercePageBreadcrumbs(navigate),
      {
        label: commerceData?.name || 'Comercio',
        onClick: () => navigate(`/business/commerce/${commerceId}/dashboard`),
      },
      {
        label: 'Configuraciones',
        onClick: () => navigate(`/business/commerce/${commerceId}/settings`),
      },
    ],
    [navigate, commerceId, commerceData?.name]
  );

  const { filters, setFilters, initialFilter } = useCommerceSettingsFilter();

  const handleSearch = useCallback(
    (searchFilters: CommerceSettingsFilters) => {
      setFilters(searchFilters);
      const url = new URL(window.location.href);
      url.searchParams.set('page', '1');
      window.history.replaceState({}, '', url.toString());
    },
    [setFilters]
  );

  return (
    <section className='flex flex-col gap-6 w-full h-full'>
      <NavigateLinks
        actualPage={`Configuraciones de ${commerceData?.name || 'Comercio'}`}
        breadcrumbs={breadcrumbs}
      />
      <div className='flex gap-2.5 justify-end'>
        <Authorize permission={ROLES.BUSINESS.admin} fallback={null}>
          <GlobalButton
            variant={GlobalButtonVariant.primaryButton}
            action={() =>
              navigate(`/business/commerce/${commerceId}/settings/new`)
            }
          >
            <AddIcon />
            Nueva Configuración
          </GlobalButton>
        </Authorize>
      </div>
      <CommerceSettingsSearchBar
        initialFilter={initialFilter}
        filter={filters}
        search={handleSearch}
      />
      <CommerceSettingsTable filters={filters} commerceId={commerceId || ''} />
    </section>
  );
};

export default CommerceSettings;
